import React, { useState, useRef } from "react";
import { Plus } from "../../icons/svg/outline";
import useOutsideClick from "../../../hooks/useOutsideClick";

/**
 * CascadeButton Component
 * A button that expands to show cascading sub-buttons vertically
 *
 * @component
 * @param {string} text - The text displayed on the main button (default: "Add")
 * @param {Object} buttons - Dictionary of button configurations where keys are button labels and values are either:
 *   - onClick function: () => handleAdd("labor")
 *   - Object with onClick, color, and icon: { onClick: () => handleAdd("labor"), color: "bg-blue-50", icon: <Plus /> }
 *   Example: {
 *     "Labor": { onClick: () => handleAdd("labor"), color: "bg-blue-50", icon: <Plus className="w-4 h-4" /> },
 *     "Material": { onClick: () => handleAdd("material"), color: "bg-green-50", icon: <Plus className="w-4 h-4" /> }
 *   }
 * @param {string} [direction='down'] - Direction to cascade: 'up' or 'down'
 * @param {string} [align='left'] - Alignment of dropdown: 'left' or 'right'
 * @param {string} [className] - Additional classes for the main button
 * @param {string} [variant='primary'] - Button style variant
 *
 * @example
 * const buttons = {
 *   "Labor": { onClick: () => handleAdd("labor"), color: "bg-blue-50", icon: <Plus className="w-4 h-4" /> },
 *   "Material": { onClick: () => handleAdd("material"), color: "bg-green-50", icon: <Plus className="w-4 h-4" /> },
 * };
 *
 * return <CascadeButton text="Add" buttons={buttons} direction="down" align="right" />
 */
const CascadeButton = ({
  text = "Add",
  buttons = {},
  direction = "down",
  align = "left",
  className = "",
  variant = "primary",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const variantClasses = {
    primary: "bg-primary text-white hover:bg-green-800",
    secondary: "bg-secondary text-white hover:bg-green-600",
    warning: "bg-yellow-600 text-white hover:bg-red-600",
    custom: "bg-white text-gray-800 hover:bg-gray-50 border border-gray-300",
  };

  // Close when clicking outside
  useOutsideClick(containerRef, () => setIsOpen(false));

  const handleMainButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const handleSubButtonClick = (buttonConfig) => {
    // Handle both function format and object format
    const onClick =
      typeof buttonConfig === "function" ? buttonConfig : buttonConfig?.onClick;

    onClick?.();
    setIsOpen(false);
  };

  const buttonEntries = Object.entries(buttons);

  return (
    <div ref={containerRef} className="relative inline-block">
      {/* Main Button */}
      <button
        onClick={handleMainButtonClick}
        className={`
          px-4 py-2 h-[24px] text-sm font-medium rounded-md
          transition-all ease-in-out
          flex items-center gap-2 cursor-pointer
          ${variantClasses[variant]}
          ${isOpen ? "relative z-50" : ""}
          ${className}
        `}
      >
        <Plus
          className={`w-4 h-4 transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
        {text}
      </button>

      {/* Sub Buttons Container */}
      {isOpen && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} ${
            direction === "down" ? "top-full mt-2" : "bottom-full mb-2"
          } z-50 flex flex-col gap-[2px] min-w-full`}
        >
          {/* Backdrop overlay positioned exactly behind the buttons */}
          <div
            className="absolute inset-0 bg-white bg-opacity-20 rounded-md z-0"
            onClick={() => setIsOpen(false)}
          />

          {buttonEntries.map(([label, buttonConfig], index) => {
            // Extract color and icon from config (if object) or use defaults
            const color =
              typeof buttonConfig === "object" && buttonConfig?.color
                ? buttonConfig.color
                : "bg-white";

            const icon =
              typeof buttonConfig === "object" && buttonConfig?.icon
                ? buttonConfig.icon
                : null;

            return (
              <button
                key={label}
                onClick={() => handleSubButtonClick(buttonConfig)}
                className={`px-4 py-[2px] h-[24px] text-sm font-medium rounded-md ${color} text-gray-800 cursor-pointer hover:brightness-95 border border-gray-300 shadow-lg hover:shadow-xl transition-all duration-200 whitespace-nowrap text-left animate-cascade-in flex items-center gap-2 relative z-10`}
                // style={{
                //   animationDelay: `${index * 50}ms`,
                //   opacity: 0,
                //   animation: `cascadeIn 100ms ease-out ${index * 50}ms forwards`,
                // }}
              >
                {icon && <span className="flex-shrink-0">{icon}</span>}
                {label}
              </button>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes cascadeIn {
          from {
            opacity: 0;
            transform: translateY(${direction === "down" ? "-10px" : "10px"});
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CascadeButton;
